import type { ReactElement } from 'react';
import { useMemo } from 'react';
import {
    defaultIntegratedWebsiteDisplayNameBySystemId,
    ThirdPartySystemId,
    ThirdPartySystemIdToName,
    type IpxCredentialDefinitionSearchListItem,
    type IpxCredentialDefinitionSearchRequest,
    type IpxCredentialDefinitionSearchResponse,
} from '@pillar-portal/api-types';
import type { Status } from '@pillar-portal/database/dist/frontend';
import { OmnisearchSelect, type OmnisearchFetchPage } from '@/components/ui/omnisearch-select';
import { ipxCredentialManagementApi } from '@/lib/pillarApi';

interface IpxCredentialDefinitionSearchPage<
    TDefinition extends IpxCredentialDefinitionSearchListItem,
> {
    readonly definitions: TDefinition[];
    readonly pagination: IpxCredentialDefinitionSearchResponse['pagination'];
}

interface IpxCredentialDefinitionOmnisearchSelectCommonProps<
    TDefinition extends IpxCredentialDefinitionSearchListItem,
> {
    readonly selectedDefinition: TDefinition | null;
    readonly selectedDefinitionUuid: string | null;
    readonly onSelect: (definition: TDefinition) => void;
    readonly onClearSelection: () => void;
    readonly label?: string;
    readonly placeholder?: string;
    readonly pageSize?: number;
    readonly disabled?: boolean;
    readonly resetToken?: string;
    readonly status?: Status;
}

interface IpxCredentialDefinitionOmnisearchSelectWithFetcherProps<
    TDefinition extends IpxCredentialDefinitionSearchListItem,
> extends IpxCredentialDefinitionOmnisearchSelectCommonProps<TDefinition> {
    readonly fetchDefinitionsPage: (
        request: IpxCredentialDefinitionSearchRequest
    ) => Promise<IpxCredentialDefinitionSearchPage<TDefinition>>;
}

interface IpxCredentialDefinitionOmnisearchSelectDefaultProps extends IpxCredentialDefinitionOmnisearchSelectCommonProps<IpxCredentialDefinitionSearchListItem> {
    readonly fetchDefinitionsPage?: undefined;
}

export function getIpxCredentialDefinitionSystemDisplayName(
    definition: IpxCredentialDefinitionSearchListItem
): string {
    if (definition.system_id === ThirdPartySystemId.GENERIC) {
        return 'Generic Website';
    }
    return (
        defaultIntegratedWebsiteDisplayNameBySystemId[definition.system_id] ??
        ThirdPartySystemIdToName[definition.system_id] ??
        `System ${definition.system_id}`
    );
}

export function formatIpxCredentialDefinitionKey(
    definition: IpxCredentialDefinitionSearchListItem
): string {
    const trimmedKey: string = definition.credential_definition_key.trim();
    return trimmedKey.length === 0 ? '(default)' : trimmedKey;
}

export function formatIpxCredentialDefinitionLabel(
    definition: IpxCredentialDefinitionSearchListItem
): string {
    return `${definition.name} - ${getIpxCredentialDefinitionSystemDisplayName(
        definition
    )} - ${formatIpxCredentialDefinitionKey(definition)}`;
}

export function getIpxCredentialDefinitionFilterText(
    definition: IpxCredentialDefinitionSearchListItem
): string {
    return `${definition.name} ${getIpxCredentialDefinitionSystemDisplayName(
        definition
    )} ${formatIpxCredentialDefinitionKey(
        definition
    )} ${definition.ipx_credential_definition_uuid}`.toLowerCase();
}

function IpxCredentialDefinitionOmnisearchSelectBase<
    TDefinition extends IpxCredentialDefinitionSearchListItem =
        IpxCredentialDefinitionSearchListItem,
>(
    props: IpxCredentialDefinitionOmnisearchSelectCommonProps<TDefinition> & {
        readonly fetchDefinitionsPage: (
            request: IpxCredentialDefinitionSearchRequest
        ) => Promise<IpxCredentialDefinitionSearchPage<TDefinition>>;
    }
): ReactElement {
    const fetchPage: OmnisearchFetchPage<TDefinition, string> = useMemo(() => {
        return async (params) => {
            const response = await props.fetchDefinitionsPage({
                ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                limit: params.limit,
                cursor: params.cursor,
                ...(props.status !== undefined ? { status: props.status } : {}),
            });
            return {
                items: response.definitions,
                pagination: response.pagination,
            };
        };
    }, [props.fetchDefinitionsPage, props.status]);

    return (
        <OmnisearchSelect<TDefinition, string>
            label={props.label ?? 'Credential Definition'}
            placeholder={props.placeholder ?? 'Search credential definitions...'}
            pageSize={props.pageSize ?? 25}
            fetchPage={fetchPage}
            getItemId={(definition) => definition.ipx_credential_definition_uuid}
            getItemLabel={formatIpxCredentialDefinitionLabel}
            getItemFilterText={getIpxCredentialDefinitionFilterText}
            renderItemCard={(definition) => (
                <div>
                    <div className="font-medium">{definition.name}</div>
                    <div className="text-xs text-muted-foreground">
                        {getIpxCredentialDefinitionSystemDisplayName(definition)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        Key: {formatIpxCredentialDefinitionKey(definition)}
                    </div>
                </div>
            )}
            onSelect={props.onSelect}
            onClearSelection={props.onClearSelection}
            selectedItemId={props.selectedDefinitionUuid}
            selectedItemLabel={
                props.selectedDefinition === null
                    ? undefined
                    : formatIpxCredentialDefinitionLabel(props.selectedDefinition)
            }
            resetToken={props.resetToken}
            disabled={props.disabled}
            fetchOnEmptyQuery
        />
    );
}

export function IpxCredentialDefinitionOmnisearchSelect(
    props: IpxCredentialDefinitionOmnisearchSelectDefaultProps
): ReactElement;
export function IpxCredentialDefinitionOmnisearchSelect<
    TDefinition extends IpxCredentialDefinitionSearchListItem,
>(props: IpxCredentialDefinitionOmnisearchSelectWithFetcherProps<TDefinition>): ReactElement;
export function IpxCredentialDefinitionOmnisearchSelect<
    TDefinition extends IpxCredentialDefinitionSearchListItem,
>(
    props:
        | IpxCredentialDefinitionOmnisearchSelectDefaultProps
        | IpxCredentialDefinitionOmnisearchSelectWithFetcherProps<TDefinition>
): ReactElement {
    if (props.fetchDefinitionsPage === undefined) {
        return (
            <IpxCredentialDefinitionOmnisearchSelectBase
                {...props}
                fetchDefinitionsPage={ipxCredentialManagementApi.searchCredentialDefinitions}
            />
        );
    }
    return <IpxCredentialDefinitionOmnisearchSelectBase {...props} />;
}
